import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, FileText, Hammer, AlertTriangle, ArrowUpRight } from "lucide-react";
import api, { money, fmtDate } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, StatusBadge } from "@/components/common";
import MonthlySales from "@/components/MonthlySales";

function Kpi({ icon: Icon, label, value, hint, testId, accent }) {
  return (
    <div className={`rounded-xl border p-5 bg-white fade-up ${accent ? "border-amber-300" : ""}`} data-testid={testId}>
      <div className="flex items-center justify-between text-stone-500">
        <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
        <Icon className="h-4 w-4 text-amber-700" />
      </div>
      <p className="mt-3 text-3xl font-display font-extrabold tracking-tight font-mono">{value}</p>
      {hint && <p className="text-xs text-stone-500 mt-1">{hint}</p>}
    </div>
  );
}

function Panel({ title, to, children, testId }) {
  return (
    <section className="rounded-xl border bg-white fade-up" data-testid={testId}>
      <div className="flex items-center justify-between px-5 py-4 border-b">
        <h3 className="font-bold">{title}</h3>
        {to && <Link to={to} className="text-xs text-amber-700 font-semibold inline-flex items-center gap-1 hover:underline">Ver todo<ArrowUpRight className="h-3 w-3" /></Link>}
      </div>
      <div className="divide-y">{children}</div>
    </section>
  );
}

const Empty = ({ text }) => <p className="px-5 py-8 text-sm text-stone-400 text-center">{text}</p>;

export default function Dashboard() {
  const { settings } = useApp();
  const [d, setD] = useState(null);
  const sym = settings?.currency_symbol || "$";

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, []);

  if (!d) return <p className="text-stone-500">Cargando…</p>;

  return (
    <div>
      <PageHeader title="Resumen del taller" subtitle={`Hoy ${fmtDate(d.today)}`} />
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 mb-8">
        <Kpi icon={Receipt} label="Ventas del mes" value={money(d.month_sales_total, sym)} hint={`${d.month_sales_count} notas`} testId="kpi-month-sales" accent />
        <Kpi icon={Receipt} label="Saldo por cobrar" value={money(d.pending_balance, sym)} hint="Notas pendientes" testId="kpi-pending-balance" />
        <Kpi icon={FileText} label="Cotizaciones abiertas" value={d.pending_quotes} hint="Borrador o enviadas" testId="kpi-pending-quotes" />
        <Kpi icon={Hammer} label="Trabajos activos" value={d.active_jobs} hint={`${d.tools_in_repair} herramientas en reparación`} testId="kpi-active-jobs" />
      </div>
      <MonthlySales sym={sym} />
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Últimas notas de venta" to="/ventas" testId="panel-recent-sales">
          {d.recent_sales.length === 0 && <Empty text="Aún no hay notas de venta" />}
          {d.recent_sales.map((s) => (
            <Link key={s.id} to={`/ventas/${s.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-stone-50">
              <div>
                <p className="text-sm font-semibold">{s.client_name}</p>
                <p className="text-xs text-stone-500 font-mono">{s.folio} · {fmtDate(s.date)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-mono font-semibold">{money(s.total, sym)}</p>
                <StatusBadge status={s.status} />
              </div>
            </Link>
          ))}
        </Panel>
        <Panel title="Próximos trabajos" to="/agenda" testId="panel-upcoming-jobs">
          {d.upcoming_jobs.length === 0 && <Empty text="Sin trabajos programados" />}
          {d.upcoming_jobs.map((j) => (
            <div key={j.id} className="flex items-center justify-between px-5 py-3">
              <div>
                <p className="text-sm font-semibold">{j.title}</p>
                <p className="text-xs text-stone-500">{j.client_name || "—"} · Inicio {fmtDate(j.start_date)}</p>
              </div>
              <StatusBadge status={j.status} />
            </div>
          ))}
        </Panel>
        <Panel title="Stock bajo" to="/materiales" testId="panel-low-stock">
          {d.low_stock.length === 0 && <Empty text="Todo el material está en orden" />}
          {d.low_stock.map((m) => (
            <div key={m.id} className="flex items-center justify-between px-5 py-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <p className="text-sm font-semibold">{m.name}</p>
              </div>
              <p className="text-xs font-mono text-red-700">{m.quantity} / mín {m.min_stock} {m.unit}</p>
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}
